import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  TextField,
  Button,
  Avatar,
  Badge,
  IconButton,
  Stack,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Page from "../components/Page";
import { db } from "../database/db";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  where,
  getDoc,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";

const ChatContainer = styled(Paper)(({ theme }) => ({
  height: "70vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f4f6f8",
  borderRadius: "12px",
  overflow: "hidden",
}));

const MessageList = styled(Box)({
  flexGrow: 1,
  overflowY: "auto",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
});

const MessageBubble = styled(Box)(({ isSelf }) => ({
  maxWidth: "70%",
  padding: "10px 16px",
  borderRadius: "18px",
  marginBottom: "10px",
  alignSelf: isSelf ? "flex-end" : "flex-start",
  backgroundColor: isSelf ? "#097936" : "#fff",
  color: isSelf ? "#fff" : "#000",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
}));

export default function Support() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("open"); // "open" or "solved"

  useEffect(() => {
    let q;
    if (filter === "open") {
      q = query(
        collection(db, "SupportTickets"),
        where("status", "==", "open"),
        orderBy("lastUpdate", "desc")
      );
    } else {
      // Show solved tickets from the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      q = query(
        collection(db, "SupportTickets"),
        where("status", "==", "solved"),
        where("solvedAt", ">=", thirtyDaysAgo),
        orderBy("solvedAt", "desc")
      );
    }
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setTickets(list);
    });
    return () => unsubscribe();
  }, [filter]);

  useEffect(() => {
    if (!selectedTicket) return;
    const q = query(
      collection(db, `SupportTickets/${selectedTicket.id}/messages`),
      orderBy("timestamp", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => doc.data());
      setMessages(list);
      // Mark as read
      updateDoc(doc(db, "SupportTickets", selectedTicket.id), { unreadAdmin: false });
    });
    return () => unsubscribe();
  }, [selectedTicket]);

  const handleSend = async () => {
    if (!reply.trim() || !selectedTicket) return;
    await addDoc(collection(db, `SupportTickets/${selectedTicket.id}/messages`), {
      text: reply,
      sender: "admin",
      timestamp: serverTimestamp(),
    });
    await updateDoc(doc(db, "SupportTickets", selectedTicket.id), {
      lastMessage: reply,
      lastUpdate: serverTimestamp(),
      unreadUser: true,
    });
    setReply("");
  };

  const markSolved = async () => {
    if (!selectedTicket) return;
    await updateDoc(doc(db, "SupportTickets", selectedTicket.id), {
      status: "solved",
      solvedAt: serverTimestamp(),
    });
    setSelectedTicket(null);
  };

  return (
    <Page title="Support | Help Desk">
      <Grid container spacing={3} sx={{ px: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "80vh", overflowY: "auto", borderRadius: "12px" }}>
            <Box sx={{ p: 2, bgcolor: "#097936", color: "#fff", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Tickets</Typography>
              <Stack direction="row" spacing={1}>
                <Button 
                  size="small" 
                  variant={filter === "open" ? "contained" : "outlined"} 
                  color="inherit" 
                  onClick={() => setFilter("open")}
                  sx={{ color: filter === "open" ? "#097936" : "#fff" }}
                >
                  Open
                </Button>
                <Button 
                  size="small" 
                  variant={filter === "solved" ? "contained" : "outlined"} 
                  color="inherit" 
                  onClick={() => setFilter("solved")}
                  sx={{ color: filter === "solved" ? "#097936" : "#fff" }}
                >
                  Solved
                </Button>
              </Stack>
            </Box>
            <List>
              {tickets.map((ticket) => (
                <React.Fragment key={ticket.id}>
                  <ListItem
                    button
                    selected={selectedTicket?.id === ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <Badge color="error" variant="dot" invisible={!ticket.unreadAdmin}>
                      <Avatar src={ticket.userImage} sx={{ mr: 2 }} />
                    </Badge>
                    <ListItemText
                      primary={ticket.userName || "User"}
                      secondary={ticket.lastMessage}
                      primaryTypographyProps={{ fontWeight: ticket.unreadAdmin ? "bold" : "normal" }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {tickets.length === 0 && (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">No open tickets</Typography>
                </Box>
              )}
            </List>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          {selectedTicket ? (
            <ChatContainer elevation={0}>
              <Box sx={{ p: 2, bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={selectedTicket.userImage} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{selectedTicket.userName}</Typography>
                    <Typography variant="caption" color="textSecondary">User ID: {selectedTicket.userId}</Typography>
                  </Box>
                </Stack>
                <Button variant="contained" color="success" onClick={markSolved}>
                  Mark Solved
                </Button>
              </Box>
              <Divider />
              <MessageList>
                {messages.map((msg, i) => (
                  <MessageBubble key={i} isSelf={msg.sender === "admin"}>
                    <Typography variant="body2">{msg.text}</Typography>
                    <Typography variant="caption" sx={{ display: "block", textAlign: "right", opacity: 0.7 }}>
                      {msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </MessageBubble>
                ))}
              </MessageList>
              <Divider />
              <Box sx={{ p: 2, bgcolor: "#fff", display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Type a response..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSend()}
                />
                <Button variant="contained" sx={{ bgcolor: "#097936" }} onClick={handleSend}>
                  Send
                </Button>
              </Box>
            </ChatContainer>
          ) : (
            <Card sx={{ height: "80vh", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "12px" }}>
              <Typography color="textSecondary">Select a ticket to view conversation</Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </Page>
  );
}
